import React, { useRef, useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerOverlay, Button, Table } from 'equal-ds-ui';
import { Download, Loader2, UploadCloud, Upload } from 'lucide-react';
import type { BulkUploadDrawerProps, BulkUploadResultSection } from './BulkUploadDrawer.types';

/** Maximum file size in bytes (10MB) */
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

function renderSection<RowType>(section: BulkUploadResultSection<RowType>, variant: 'error' | 'success') {
  const hasRows = section.rows.length > 0;
  const accent =
    variant === 'error'
      ? 'border-red-200 bg-red-50 text-red-700'
      : 'border-green-200 bg-green-50 text-green-700';

  return (
    <div className="mt-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">
            {section.title}
          </h3>
        </div>
        {section.description ? (
          <div className={`text-sm border rounded-lg px-3 py-2 ${accent}`}>
            {section.description}
          </div>
        ) : null}
      </div>

      <div className="mt-4">
        {hasRows ? (
          <Table
            columns={section.columns}
            data={section.rows}
            className="shadow-sm border border-border-default rounded-lg"
            {...(section.tableProps as Record<string, unknown>)}
          />
        ) : (
          section.emptyState ?? (
            <div className="border border-dashed border-border-light rounded-lg py-8 text-center text-sm text-text-secondary bg-background-tertiary">
              No records available.
            </div>
          )
        )}
      </div>

      {section.footer ? <div className="mt-3">{section.footer}</div> : null}
    </div>
  );
}

export const BulkUploadDrawer = <
  SuccessRow = any,
  ErrorRow = any
>(
  props: BulkUploadDrawerProps<SuccessRow, ErrorRow>
) => {
  const {
    isOpen,
    title,
    description,
    onClose,
    sampleFile,
    fileInput,
    validateConfig,
    isBusy,
    errorSection,
    successSection,
    footerActions,
    contentSlot,
  } = props;

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  if (!isOpen) {
    return null;
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        if (import.meta.env.DEV) {
          console.warn(`[BulkUploadDrawer] File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Max: 10MB`);
        }
        alert('File size exceeds 10MB limit. Please select a smaller file.');
        event.target.value = '';
        return;
      }
      fileInput.onFileSelect(file);
      // Reset value to allow selecting the same file twice
      event.target.value = '';
    }
  };

  const triggerFileDialog = () => {
    if (fileInput.disabled) {
      return;
    }
    fileInputRef.current?.click();
  };

  // Drag and drop handlers
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!fileInput.disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    if (fileInput.disabled) {
      return;
    }

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      // Validate file size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        if (import.meta.env.DEV) {
          console.warn(`[BulkUploadDrawer] File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Max: 10MB`);
        }
        alert('File size exceeds 10MB limit. Please select a smaller file.');
        return;
      }

      // Validate file type if accept is specified
      const acceptTypes = (fileInput.accept ?? '.csv,.xlsx').split(',').map(t => t.trim());
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const isValidType = acceptTypes.some(type =>
        type === fileExtension || type === file.type
      );

      if (isValidType) {
        fileInput.onFileSelect(file);
      } else {
        if (import.meta.env.DEV) {
          console.warn(`[BulkUploadDrawer] Invalid file type: ${file.name}. Expected: ${fileInput.accept}`);
        }
        alert(`Invalid file type. Please upload a ${fileInput.accept ?? '.csv,.xlsx'} file.`);
      }
    }
  };

  return (
    <>
      <DrawerOverlay />
      <Drawer variant="overlay" width="900px">
        <DrawerContent className="equal-ds-root flex flex-col h-full max-h-screen" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F2F8FB 100%)' }}>
          {/* Use DrawerHeader with proper props - close button will be positioned correctly */}
          <DrawerHeader
            title={title}
            subtitle={typeof description === 'string' ? description : undefined}
            icon={<Upload className="w-5 h-5" />}
            onClose={onClose}
          />

          <div className="relative flex-1 overflow-y-auto px-6 py-5">
            {isBusy ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                  <span className="text-sm text-text-secondary">Processing...</span>
                </div>
              </div>
            ) : null}

            <div className={`${isBusy ? 'pointer-events-none opacity-50' : ''}`}>
              {/* Upload Section */}
              <section className="border border-border-default rounded-xl p-5 bg-white shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-text-primary">
                    Upload file
                  </h3>
                  {sampleFile ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={sampleFile.onDownload}
                      className="bg-white text-text-primary border border-border-default hover:bg-background-tertiary"
                    >
                      {sampleFile.icon ?? <Download className="w-4 h-4" />}
                      <span>{sampleFile.label ?? 'Download Sample'}</span>
                    </Button>
                  ) : null}
                </div>

                {fileInput.helperText ? (
                  <p className="text-sm text-text-secondary mb-4">
                    {fileInput.helperText}
                  </p>
                ) : null}

                {/* File Upload Area with Drag and Drop */}
                <div
                  className={`border-2 border-dashed rounded-lg p-6 transition-colors cursor-pointer ${
                    isDragOver
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-border-default bg-background-tertiary hover:bg-background-secondary'
                  } ${fileInput.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={triggerFileDialog}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                      isDragOver ? 'bg-primary-200' : 'bg-primary-100'
                    }`}>
                      <UploadCloud className={`w-6 h-6 ${isDragOver ? 'text-primary-700' : 'text-primary-600'}`} />
                    </div>
                    <p className="text-sm font-medium text-text-primary mb-1">
                      {isDragOver ? 'Drop file here' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-xs text-text-tertiary mb-4">
                      {fileInput.accept === '.csv'
                        ? 'CSV files only (max 10MB)'
                        : 'CSV or Excel files (max 10MB)'}
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={fileInput.accept ?? '.csv,.xlsx'}
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="secondary"
                        size="md"
                        onClick={triggerFileDialog}
                        disabled={fileInput.disabled}
                      >
                        <UploadCloud className="w-4 h-4" />
                        <span>{fileInput.label ?? 'Select File'}</span>
                      </Button>
                      {validateConfig ? (
                        <Button
                          variant="primary"
                          size="md"
                          onClick={validateConfig.onValidate}
                          disabled={validateConfig.disabled}
                        >
                          {validateConfig.label ?? 'Validate'}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>

                {fileInput.note ? (
                  <p className="mt-3 text-sm text-text-secondary">
                    {fileInput.note}
                  </p>
                ) : null}
              </section>

              {contentSlot ? <div className="mt-5">{contentSlot}</div> : null}

              {errorSection ? renderSection(errorSection, 'error') : null}
              {successSection ? renderSection(successSection, 'success') : null}
            </div>
          </div>

          {/* Footer Actions */}
          {footerActions ? (
            <div className="border-t border-border-default px-6 py-4 bg-background-secondary flex-shrink-0">
              <div className="flex items-center justify-end gap-3">
                {footerActions.secondary ? (
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={footerActions.secondary.onClick}
                    disabled={footerActions.secondary.disabled}
                    className="bg-white text-text-primary border border-border-default hover:bg-background-tertiary min-w-[100px]"
                  >
                    {footerActions.secondary.label}
                  </Button>
                ) : null}
                {footerActions.additionalActions}
                {footerActions.primary ? (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={footerActions.primary.onClick}
                    disabled={footerActions.primary.disabled}
                    className="min-w-[120px]"
                  >
                    {footerActions.primary.label}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
        </DrawerContent>
      </Drawer>
    </>
  );
};

BulkUploadDrawer.displayName = 'BulkUploadDrawer';
